import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, X, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Seo from "@/components/Seo";

type Phase = "checking" | "ready" | "invalid" | "done";

const ResetPasswordPage = () => {
  const [phase, setPhase] = useState<Phase>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let settled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) {
        settled = true;
        setPhase("ready");
      }
    });

    (async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const errorDescription =
        url.searchParams.get("error_description") || hash.get("error_description");

      if (errorDescription) {
        setPhase("invalid");
        return;
      }

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          setPhase("invalid");
          return;
        }
        settled = true;
        setPhase("ready");
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (data.session) {
        settled = true;
        setPhase("ready");
        return;
      }

      // Hash-token hanteras av klienten strax efter mount — ge den en stund.
      setTimeout(() => {
        if (!settled) setPhase((p) => (p === "checking" ? "invalid" : p));
      }, 2500);
    })();

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (password.length < 8) {
      toast.error("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    if (password !== confirm) {
      toast.error("Lösenorden matchar inte.");
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPhase("done");
    toast.success("Lösenordet är uppdaterat!");
    setTimeout(() => navigate("/account"), 1200);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-coral-400/30 via-terracotta-100/40 to-cream-100">
      <Seo
        title="Välj nytt lösenord – Aquadias"
        description="Sätt ett nytt lösenord för ditt aQuadias-konto."
        path="/reset-password"
      />

      <div className="flex justify-between items-center px-6 py-4">
        <Link to="/login" className="p-2 rounded-full hover:bg-white/30 transition-colors">
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </Link>
        <Link to="/" className="text-xl font-bold tracking-wide text-foreground">aQuadias</Link>
        <Link to="/" className="p-2 rounded-full hover:bg-white/30 transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md space-y-8 text-center">
          <div>
            <span className="font-serif italic text-5xl md:text-6xl text-foreground">Nytt</span>
            <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-wider text-foreground mt-2">
              lösenord
            </h1>
          </div>

          {phase === "checking" && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-foreground/60" />
            </div>
          )}

          {phase === "invalid" && (
            <div className="space-y-6 text-left">
              <p className="text-sm text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-5 py-4">
                Länken har gått ut eller är redan använd. Begär en ny länk så skickar vi ett nytt
                mejl.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-widest text-foreground/70 hover:text-foreground transition-colors"
              >
                Till inloggning <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          {phase === "done" && (
            <p className="text-sm text-foreground/80 bg-white/40 backdrop-blur-sm rounded-lg px-5 py-4 text-left">
              Klart! Ditt lösenord är sparat och vi tar dig vidare till ditt konto.
            </p>
          )}

          {phase === "ready" && (
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="NYTT LÖSENORD"
                  minLength={8}
                  className="w-full bg-white/50 backdrop-blur-sm border-0 rounded-lg px-5 py-4 pr-12 text-sm tracking-widest placeholder:text-muted-foreground/70 placeholder:tracking-widest placeholder:uppercase focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <input
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="UPPREPA LÖSENORD"
                minLength={8}
                className="w-full bg-white/50 backdrop-blur-sm border-0 rounded-lg px-5 py-4 text-sm tracking-widest placeholder:text-muted-foreground/70 placeholder:tracking-widest placeholder:uppercase focus:outline-none focus:ring-2 focus:ring-ring/30 transition-all"
                required
              />

              <p className="px-2 text-xs text-muted-foreground">Minst 8 tecken.</p>

              <button
                type="submit"
                disabled={saving}
                className="w-full border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-sm hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Spara lösenord <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
